import Component from "@glimmer/component";
import PageContent from "glesys-controlpanel/components/page-content";
import PageContentServiceIntroDetails from "glesys-controlpanel/components/page-content/service-intro-details";
import IconsMoreHorizontal from "glesys-controlpanel/components/icons/more-horizontal";
import Dropdown from "glesys-controlpanel/components/dropdown";
import DropdownDestructiveItem from "glesys-controlpanel/components/dropdown/destructive-item";
import GTabs from "glesys-controlpanel/components/g-tabs";
import { t } from "ember-intl";
import canManage from "glesys-controlpanel/helpers/can-manage";
import { restartableTask, task, timeout } from "ember-concurrency";
import { service } from "@ember/service";
import ENV from "glesys-controlpanel/config/environment";
import { on } from "@ember/modifier";
import { modifier } from "ember-modifier";
import modal from "glesys-controlpanel/helpers/modal";
import DataLoader from "glesys-controlpanel/components/data-loader";
import Header from "./components/header";
import DatabaseResourcesBox from "./components/resources-box";
import DatabaseBackupBox from "./components/backup-box";
import DatabaseCostBox from "./components/cost-box";
import { findRecord } from "@warp-drive/legacy/compat/builders";

export default class extends Component {
  @service http;
  @service intl;
  @service abilities;
  @service store;

  get database() {
    return this.args.model.database;
  }

  get dataCenters() {
    return this.args.model.dataCenters;
  }

  get project() {
    return this.args.model.project;
  }

  get tabs() {
    return [
      { name: this.intl.t("databases.details.tabs.overview"), route: "databases.details.overview" },
      {
        name: this.intl.t("databases.details.tabs.databases-and-users"),
        route: "databases.details.databases-and-users",
      },
    ];
  }

  startPoll = modifier(() => {
    this.pollDetailsTask.perform();

    return () => this.pollDetailsTask.cancelAll();
  });

  // istanbul ignore next
  pollDetailsTask = task(async () => {
    if (ENV.environment === "test") {
      return;
    }

    while (this.database.isCreating || this.database.isUpdating) {
      await timeout(30 * 1000);

      await this.store.request(findRecord("database", this.database.id, { reload: true }));
    }
  });

  fetchEstimatedCostTask = restartableTask(async () => {
    if (this.abilities.cannot("access pricing for organization", this.project.organization)) {
      return {
        current: {
          price: null,
          total: null,
        },
        currency: null,
      };
    }

    const response = await this.http.request({
      url: `${ENV.apiBaseUri}/database/estimatedcost?projectkey=${this.project.id}&databaseid=${this.database.id}`,
      method: "POST",
    });

    return response.content.billing;
  });

  <template>
    <PageContent {{this.startPoll}}>
      <PageContentServiceIntroDetails class="border-none" @title={{this.database.name}} @key={{this.database.id}}>
        {{#if (canManage)}}
          <Dropdown
            class="w-9 h-8 cursor-pointer flex items-center justify-center rounded-sm bg-white border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800"
            aria-label={{t "label.action-menu-aria-label" name=this.database.name}}
            data-test-database-details-menu
            @chevron={{false}}
          >
            <:header>
              <IconsMoreHorizontal class="w-12" />
            </:header>
            <:default>
              <DropdownDestructiveItem
                data-test-database-delete
                {{on
                  "click"
                  (modal
                    "modals/organism-delete"
                    entity=this.database.name
                    task=this.database.deleteTask
                    entityKey=this.database.id
                    leadText=(t "databases.delete-warning")
                  )
                }}
              >
                {{t "button.delete"}}
              </DropdownDestructiveItem>
            </:default>
          </Dropdown>
        {{/if}}
      </PageContentServiceIntroDetails>

      <div class="flex flex-wrap items-center gap-4 text-black -mt-10 pb-5 border-b border-gray-200">
        <Header @database={{this.database}} @dataCenters={{this.dataCenters}} />
      </div>

      <div class="grid md:grid-cols-3 gap-8 mt-8">
        <DatabaseResourcesBox @database={{this.database}} />
        <DatabaseBackupBox @database={{this.database}} />
        <DataLoader @load={{this.fetchEstimatedCostTask}} @useLoadingSkeleton={{false}} as |cost|>
          <DatabaseCostBox @cost={{cost}} />
        </DataLoader>
      </div>

      <GTabs class="mt-8" @tabs={{this.tabs}} />

      {{outlet}}
    </PageContent>
  </template>
}
